import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../utils/api';
import ServicePerformanceTable from '../components/ServicePerformanceTable';
import TransactionsTable from '../components/TransactionsTable';

function ReportsPage() {
  const [transactions, setTransactions] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [servicePerformance, setServicePerformance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    service_id: '',
    payment_method: '',
    payment_status: ''
  });

  useEffect(() => {
    fetchReports();
  }, [filters]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [transactionsRes, revenueRes, performanceRes] = await Promise.all([
        reportsAPI.getTransactions(filters),
        reportsAPI.getRevenue(filters),
        reportsAPI.getServicesPerformance(filters)
      ]);

      setTransactions(transactionsRes.data);
      setRevenue(revenueRes.data);
      setServicePerformance(performanceRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Customer', 'Service', 'Amount', 'Payment Method', 'Status'];
    const rows = transactions.map(t => [
      t.booking_date,
      t.customer_name,
      t.service_name,
      t.amount,
      t.payment_method,
      t.payment_status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="container">
      <h1 style={{ marginBottom: '2rem' }}>Reports & Analytics</h1>

      {/* Revenue Summary */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <div className="value">£{revenue?.total_revenue?.toFixed(2) || '0.00'}</div>
          <div className="subtitle">{revenue?.total_transactions || 0} transactions</div>
        </div>
        <div className="stat-card">
          <h3>Card Payments</h3>
          <div className="value">£{revenue?.card_revenue?.toFixed(2) || '0.00'}</div>
          <div className="subtitle">{revenue?.card_count || 0} transactions</div>
        </div>
        <div className="stat-card">
          <h3>Cash Payments</h3>
          <div className="value">£{revenue?.cash_revenue?.toFixed(2) || '0.00'}</div>
          <div className="subtitle">{revenue?.cash_count || 0} transactions</div>
        </div>
        <div className="stat-card">
          <h3>Average Transaction</h3>
          <div className="value">£{revenue?.average_transaction?.toFixed(2) || '0.00'}</div>
        </div>
      </div>

      {/* Service Performance */}
      <div className="card">
        <h2 style={{ marginBottom: '1.5rem' }}>Service Performance</h2>
        <ServicePerformanceTable services={servicePerformance} />
      </div>

      {/* Transaction History */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Transaction History</h2>
          <button className="btn btn-primary" onClick={exportToCSV}>
            Export to CSV
          </button>
        </div>

        {/* Filters */}
        <div className="filters">
          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
            />
          </div>
          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
            />
          </div>
          <div className="form-group">
            <label>Payment Method</label>
            <select name="payment_method" value={filters.payment_method} onChange={handleFilterChange}>
              <option value="">All</option>
              <option value="card">Card</option>
              <option value="cash">Cash</option>
            </select>
          </div>
          <div className="form-group">
            <label>Payment Status</label>
            <select name="payment_status" value={filters.payment_status} onChange={handleFilterChange}>
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading transactions...</p>
          </div>
        ) : (
          <TransactionsTable transactions={transactions} />
        )}
      </div>
    </div>
  );
}

export default ReportsPage;
